import { Controller } from "@hotwired/stimulus"
import L from "leaflet"

const EMERALD_500 = "#10b981"
const EMERALD_600 = "#059669"
const EMERALD_700 = "#047857"

export default class extends Controller {
  static values = {
    locations: Array
  }

  connect() {
    if (!this.map) {
      this.initializeMap()
    }
  }

  locationsValueChanged() {
    if (this.map && this.locationsValue?.length > 0) {
      this.addMarkers()
    }
  }

  initializeMap() {
    if (this.element._leaflet_id) {
      return
    }

    this.map = L.map(this.element, {
      zoomControl: true,
      attributionControl: true
    }).setView([52.1326, 5.2913], 7)

    L.tileLayer('https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png', {
      maxZoom: 19,
      attribution: '&copy; OpenStreetMap &copy; CARTO'
    }).addTo(this.map)

    this.customIcon = L.divIcon({
      html: `<div class="tv-pin">
        <svg width="36" height="44" viewBox="0 0 36 44" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M18 0C8.06 0 0 8.06 0 18c0 13.5 18 26 18 26s18-12.5 18-26C36 8.06 27.94 0 18 0z" fill="${EMERALD_600}"/>
          <path d="M18 2C9.16 2 2 9.16 2 18c0 11.4 14.8 22.5 16 23.4 1.2-.9 16-12 16-23.4C34 9.16 26.84 2 18 2z" fill="${EMERALD_500}" opacity="0.95"/>
          <circle cx="18" cy="17" r="6" fill="white"/>
        </svg>
      </div>`,
      className: 'tv-pin-wrapper',
      iconSize: [36, 44],
      iconAnchor: [18, 44],
      popupAnchor: [0, -40]
    })

    this.map.on('zoomend', () => {
      this.updateMarkers()
      this.notifyViewportChange()
    })
    this.map.on('moveend', () => this.notifyViewportChange())

    if (this.locationsValue?.length > 0) {
      this.addMarkers()
    }
  }

  addMarkers() {
    if (!this.locationsValue?.length) return

    this.allMarkers = []
    const seenCounts = {}

    this.locationsValue.forEach(location => {
      const lat = parseFloat(location.latitude)
      const lng = parseFloat(location.longitude)

      if (isNaN(lat) || isNaN(lng)) return
      if (lat < 50 || lat > 54 || lng < 3 || lng > 8) return

      const key = `${lat.toFixed(5)},${lng.toFixed(5)}`
      const idx = seenCounts[key] || 0
      seenCounts[key] = idx + 1

      let pinLat = lat
      let pinLng = lng
      if (idx > 0) {
        const ring = Math.ceil(idx / 8)
        const slot = (idx - 1) % 8
        const angle = slot * (Math.PI / 4)
        const radius = 0.0008 * ring
        pinLat = lat + Math.cos(angle) * radius
        pinLng = lng + Math.sin(angle) * radius
      }

      this.allMarkers.push({
        lat: pinLat,
        lng: pinLng,
        name: location.company_name || 'Rijschool',
        slug: location.slug
      })
    })

    this.updateMarkers()
    this.notifyViewportChange()
  }

  updateMarkers() {
    if (this.markerLayer) {
      this.map.removeLayer(this.markerLayer)
    }

    this.markerLayer = L.layerGroup()
    const zoom = this.map.getZoom()

    if (zoom >= 10) {
      this.allMarkers.forEach(loc => {
        const marker = L.marker([loc.lat, loc.lng], { icon: this.customIcon })
        marker.bindPopup(this.popupHtml(loc))
        this.markerLayer.addLayer(marker)
      })
    } else {
      const clusters = this.createClusters(this.allMarkers, zoom)

      clusters.forEach(cluster => {
        if (cluster.count === 1) {
          const marker = L.marker([cluster.lat, cluster.lng], { icon: this.customIcon })
          marker.bindPopup(this.popupHtml(cluster))
          this.markerLayer.addLayer(marker)
        } else {
          const size = cluster.count >= 50 ? 52 : cluster.count >= 10 ? 46 : 40
          const clusterIcon = L.divIcon({
            html: `<div style="width:${size}px;height:${size}px;border-radius:50%;background:linear-gradient(135deg,${EMERALD_500},${EMERALD_700});color:white;display:flex;align-items:center;justify-content:center;font-weight:700;font-size:14px;border:3px solid white;box-shadow:0 4px 14px rgba(5,150,105,0.4);">${cluster.count}</div>`,
            className: 'tv-cluster',
            iconSize: [size, size]
          })

          const marker = L.marker([cluster.lat, cluster.lng], { icon: clusterIcon })
          marker.on('click', () => {
            this.map.setView([cluster.lat, cluster.lng], Math.min(zoom + 2, 14))
          })
          this.markerLayer.addLayer(marker)
        }
      })
    }

    this.markerLayer.addTo(this.map)
  }

  popupHtml(loc) {
    const safeName = (loc.name || 'Rijschool').replace(/</g, '&lt;')
    const link = loc.slug
      ? `<a href="/companies/${encodeURIComponent(loc.slug)}" data-turbo="false" style="display:inline-block;margin-top:8px;padding:6px 12px;background:${EMERALD_600};color:white;border-radius:6px;font-size:13px;font-weight:500;text-decoration:none;">Meer info</a>`
      : ''
    return `<div style="padding:6px 4px;min-width:160px;">
      <h3 style="font-size:15px;font-weight:600;color:#111827;margin:0 0 4px;">${safeName}</h3>
      ${link}
    </div>`
  }

  createClusters(markers, zoom) {
    const gridSize = zoom < 7 ? 1.0 : zoom < 8 ? 0.5 : 0.2

    const grid = {}

    markers.forEach(marker => {
      const gridX = Math.floor(marker.lat / gridSize)
      const gridY = Math.floor(marker.lng / gridSize)
      const key = `${gridX},${gridY}`

      if (!grid[key]) {
        grid[key] = []
      }
      grid[key].push(marker)
    })

    return Object.values(grid).map(group => {
      const lat = group.reduce((sum, m) => sum + m.lat, 0) / group.length
      const lng = group.reduce((sum, m) => sum + m.lng, 0) / group.length

      return {
        lat,
        lng,
        count: group.length,
        name: group[0].name,
        slug: group.length === 1 ? group[0].slug : null
      }
    })
  }

  notifyViewportChange() {
    if (!this.map || !this.allMarkers) return
    const bounds = this.map.getBounds()
    const visibleSlugs = this.allMarkers
      .filter(m => bounds.contains([m.lat, m.lng]))
      .map(m => m.slug)
      .filter(Boolean)

    window.dispatchEvent(new CustomEvent('map:viewport-changed', {
      detail: {
        slugs: visibleSlugs,
        bounds: {
          north: bounds.getNorth(),
          south: bounds.getSouth(),
          east: bounds.getEast(),
          west: bounds.getWest()
        }
      }
    }))
  }

  disconnect() {
    if (this.map) {
      this.map.remove()
      this.map = null
      this.markerLayer = null
    }
  }
}
